import {Injectable}    from '@angular/core';
import {Router} from "@angular/router";
import {JwtHelperService } from '@auth0/angular-jwt';
import {Observable} from 'rxjs/Rx';

import {Role} from "./dtos/enums/role.enum";
import {AccessTokenPayloadDto} from "./dtos/access-token-payload.dto";

import {CloudApiService} from "./cloud-api.service";
import {UserSessionService} from "./user-session.service";
import { LibraryGroupDto } from './dtos/library-group.dto';

@Injectable()
export class AuthenticationService {

    constructor(
        private router: Router,
        private userSession: UserSessionService,
        private cloudApiService: CloudApiService,
        private jwtHelper: JwtHelperService
    ) {
        cloudApiService.onUnauthorised = (isFromBadLogin: boolean) => this.onUnauthorised(isFromBadLogin);
    }

    authenticated: boolean;

    isInGlobalRole(role: number): boolean {
        let globalRoles = JSON.parse(this.accessTokenPayload.globalRoles + '');

        return globalRoles && globalRoles.find((x: number) => x == role) != undefined;
    };

    isInAnyLibraryRole(libraryId: number): boolean {
        let libraryGroupRoles = JSON.parse(this.accessTokenPayload.libraryGroupRoles + '');

        return libraryGroupRoles &&
            libraryGroupRoles.find((x: { LibraryGroupId: number; }) => x.LibraryGroupId == libraryId) != undefined;
    }
    
    isInLibraryRole(libraryId: number, role: number): boolean {
        if (this.isInGlobalRole(role))
            return true;

        let libraryGroupRoles = JSON.parse(this.accessTokenPayload.libraryGroupRoles + '');

        return libraryGroupRoles &&
            libraryGroupRoles.find((x: { LibraryGroupId: number; Role: number; }) => x.LibraryGroupId == libraryId &&
                x.Role == role) != undefined;
    }

    authenticateUser(username: string, password: string, captchaToken: string): Observable<boolean> {
        let authData = {
            username: username,
            password: password,
            captchaToken: captchaToken
        };

        return this.authenticate('password', authData);
    };

    authenticateRefreshToken(): Observable<boolean> {
        let refreshToken = localStorage.getItem(this.refreshTokenStorage);
        if (!refreshToken)
            return Observable.of(false);

        let authData = { refresh_token: refreshToken };
        return this.authenticate('refresh_token', authData)
            .catch(error => Observable.of(false));
    }

    logout(redirectToLogin: boolean = true): void {
        this.removeTokens();
        this.loadTokens();

        if (redirectToLogin)
            this.router.navigate(['/login']);
    }

    loadTokens(): void {
        try {
            let accessToken = localStorage.getItem(this.accessTokenStorage);

            this.accessTokenPayload = accessToken ? this.jwtHelper.decodeToken(accessToken) : null;
            this.refreshToken = localStorage.getItem(this.refreshTokenStorage);
            this.authenticated = !this.jwtHelper.isTokenExpired(accessToken);

            // Refresh token 10 minutes before expiry
            if (this.authenticated) {
                let expiryDate = this.jwtHelper.getTokenExpirationDate(accessToken);
                let msUntilExpired = expiryDate.getTime() - Date.now() - (10 * 60 * 1000);

                window.clearTimeout(this.refreshTokenTimerId);
                this.refreshTokenTimerId = window.setTimeout(() => {
                    console.info('Refreshing access token...');
                    this.authenticateRefreshToken().subscribe();
                }, msUntilExpired);
            }

            // Lookup user details from server
            this.loadUser();

        } catch(exception) {
            console.error('Failed to load access token', exception);
            this.removeTokens();
        }
    };

    /*** Private ***/

    private clientId: string = 'FeWebAppClient';
    private appDescription: string = 'Javascript Client - ' + navigator.userAgent;

    private accessTokenStorage: string = 'accessToken';
    private refreshTokenStorage: string = 'refreshToken';

    private refreshTokenTimerId: number;

    accessTokenPayload: AccessTokenPayloadDto;
    private refreshToken: string;

    private authenticate(grantType: string, data: Object): Observable<boolean> {
        let baseData = {
            client_id: this.clientId,
            app_description: this.appDescription,
            grant_type: grantType
        };

        Object.assign(baseData, data);

        return this.cloudApiService.authenticate(baseData)
            .map(authToken => {
                localStorage.setItem(this.accessTokenStorage, authToken.access_token);
                localStorage.setItem(this.refreshTokenStorage, authToken.refresh_token);

                this.loadTokens();
                return this.authenticated;
            });
    }

    private onUnauthorised(isFromBadLogin: boolean): void {
        this.logout(!isFromBadLogin);
    }

    private removeTokens(): void {
        window.clearTimeout(this.refreshTokenTimerId);
        if (this.refreshToken)
            this.cloudApiService.deleteRefreshToken(this.refreshToken).subscribe();

        localStorage.removeItem(this.accessTokenStorage);
        localStorage.removeItem(this.refreshTokenStorage);

        this.accessTokenPayload = null;
        this.refreshToken = null;
        this.authenticated = false;
    };

    private loadUser(): void {

        if (this.accessTokenPayload && this.accessTokenPayload.userId) {

            // Load the logged in users details
            this.cloudApiService.getUser(this.accessTokenPayload.userId)
                .subscribe(
                    user => this.userSession.setUser(user),
                    error => {
                        console.error('Failed to load logged in user', error);
                        this.userSession.setUser(null);
                    }
                );

            // If the user has access to the last selected library also load it
            if (this.userSession.libraryId) {
                this.cloudApiService.getLibrary(this.userSession.libraryId)
                    .subscribe(
                        library => this.userSession.setLibrary(library),
                        error => {
                            console.error('Failed to load selected library', error);
                            this.userSession.setLibrary(null);
                        }
                    );
            }
        } else
            this.userSession.clearSession();
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/services/authentication.service.ts